import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { exportAgentArtifact } from "../../lib/api/agentChat";
import { artifactFilename, saveBlob } from "../../lib/download";
import { remarkCitations } from "../../lib/markdown/remarkCitations";
import type { Citation, EvArtifact } from "../../types/agent-chat";
import { markdownComponents } from "./markdownComponents";
import { artifactSubtitle, artifactTitle } from "./artifactFormat";

export interface ArtifactPanelProps {
  artifact: EvArtifact;
  citations: Record<string, Citation>;
  onClose: () => void;
  onOpenDocument?: (citation: Citation) => void;
  /** Conversation the artifact belongs to; needed to export it. */
  conversationId?: string | null;
  /** False until the turn is persisted (the export reads the stored artifact). */
  canExport?: boolean;
}

/**
 * Side panel showing one generated document, the way a canvas sits next to
 * the conversation in other assistants. The chat keeps the summary; this is
 * the single place the full document is rendered.
 */
export function ArtifactPanel({
  artifact,
  citations,
  onClose,
  onOpenDocument,
  conversationId = null,
  canExport = false,
}: ArtifactPanelProps) {
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const download = async () => {
    if (!conversationId || !canExport || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const { blob, filename } = await exportAgentArtifact(
        conversationId,
        artifact.tool_call_id,
        artifactFilename(artifact)
      );
      saveBlob(blob, filename);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Le téléchargement a échoué. Réessayez.");
    } finally {
      setExporting(false);
    }
  };

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(artifact.markdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be denied; the button simply does nothing then.
    }
  };

  return (
    <aside
      aria-label="Document généré"
      className="flex h-full min-h-0 flex-col border-l border-brand-border bg-brand-bg"
    >
      <header className="flex items-start gap-3 border-b border-brand-border px-5 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-brand-text">{artifactTitle(artifact)}</p>
          <p className="truncate text-xs text-brand-muted">{artifactSubtitle(artifact)}</p>
        </div>
        <button
          type="button"
          onClick={copyMarkdown}
          className="rounded-md border border-brand-border px-2 py-1 text-xs font-semibold text-brand-text hover:bg-brand-surface"
        >
          {copied ? "Copié" : "Copier"}
        </button>
        <button
          type="button"
          onClick={download}
          disabled={!canExport || !conversationId || exporting}
          title={canExport ? "Télécharger le document au format Word" : "Disponible une fois la réponse terminée"}
          className="rounded-md border border-brand-border px-2 py-1 text-xs font-semibold text-brand-text hover:bg-brand-surface disabled:cursor-not-allowed disabled:opacity-50"
        >
          {exporting ? "Téléchargement…" : "Télécharger (.docx)"}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer le document"
          className="flex h-7 w-7 items-center justify-center rounded-md text-brand-muted hover:bg-brand-surface hover:text-brand-text"
        >
          ×
        </button>
      </header>

      {exportError && (
        <p role="alert" className="border-b border-brand-border bg-brand-error/10 px-5 py-2 text-xs text-brand-error">
          {exportError}
        </p>
      )}

      <div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {artifact.warnings.length > 0 && (
          <ul className="mb-4 list-disc rounded-lg bg-brand-accent/10 py-2 pl-8 pr-3 text-xs text-brand-accent-dk">
            {artifact.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
        <div className="prose prose-sm max-w-none text-left text-brand-text prose-strong:text-brand-text">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkCitations]}
            components={markdownComponents(citations, onOpenDocument)}
          >
            {artifact.markdown}
          </ReactMarkdown>
        </div>
      </div>
    </aside>
  );
}
